import { useEffect, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import { ModalCancelButton } from '../../components/ModalCancelButton';

/**
 * 프리셋 저장 입력 payload — 서버 createWidgetPreset body(ownerCompanyId 제외, v1=플랫폼).
 * key는 서버 자체 발급(`preset-<random8>`) — 저작 UI는 보내지 않음(2026-07-19 사용자 결정,
 * 정본 참조는 불변 id라 key는 관리용 슬러그일 뿐 입력 마찰만 유발).
 */
export interface SaveAsPresetInput {
  name: string;
  description?: string;
  changeNote: string;
}

interface SaveAsPresetModalProps {
  /** 열림 여부. false면 렌더 안 함. */
  open: boolean;
  /** 요청 진행 중 — 확인 버튼 비활성(중복 클릭 방지). */
  busy: boolean;
  /**
   * fork 안내 — presetRef 있는 인스턴스를 저장할 때 true. normalizeSubtreeForPresetSave가 루트
   * 출처를 제거해 새 프리셋(fork)이 됨을 한 줄로 안내한다.
   */
  forkNotice: boolean;
  /**
   * 이 서브트리에서 **프리셋에 담기지 않을** 파생(바인딩 배치) 입력 수 — rc
   * countPresetExcludedDerivedInputs 산출. 0이면 표시하지 않는다.
   * 저장 자체는 막지 않고 결정 전에 알린다(2026-08-19 도그푸딩: 파생 입력 8개짜리 섹션을 저장하니
   * 삽입 결과가 빈 섹션인데 아무 안내가 없어 원인 추적에 시간이 들었다).
   */
  excludedDerivedCount: number;
  onConfirm: (input: SaveAsPresetInput) => void;
  onClose: () => void;
}

/**
 * 위젯 서브트리를 프리셋으로 저장(=v1 발행)하는 입력 모달.
 * - name 필수 / description 선택 / changeNote 필수(버전 선택 판단 근거). key는 서버 자체 발급.
 * - @safetyhub/ui Modal 기반(포탈·포커스 트랩·Esc/오버레이 닫기). 확인은 명시 버튼(폼 Enter 미침해).
 */
export function SaveAsPresetModal({
  open,
  busy,
  forkNotice,
  excludedDerivedCount,
  onConfirm,
  onClose,
}: SaveAsPresetModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [changeNote, setChangeNote] = useState('');

  // 열릴 때마다 초기화(직전 입력 잔존 방지).
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setChangeNote('');
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedNote = changeNote.trim();
  const canConfirm = !!trimmedName && !!trimmedNote && !busy;
  // 미저장 닫기 보호 — 열 때마다 세 칸을 비우므로(위 useEffect) 기준선은 빈 폼이다.
  const isDirty = name !== '' || description !== '' || changeNote !== '';

  const submit = () => {
    if (!canConfirm) return;
    onConfirm({
      name: trimmedName,
      description: description.trim() || undefined,
      changeNote: trimmedNote,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="프리셋으로 저장" size="md" isDirty={isDirty}>
      <div className="space-y-3">
        {forkNotice ? (
          <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
            이 위젯은 다른 프리셋에서 삽입된 인스턴스입니다. 프리셋으로 저장하면 원본과의 연결을 끊고
            <b> 새 프리셋</b>으로 만듭니다(원본은 그대로 유지됩니다).
          </p>
        ) : null}
        {excludedDerivedCount > 0 ? (
          <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
            이 안의 <b>바인딩 배치 입력 {excludedDerivedCount}개</b>는 프리셋에 담기지 않습니다 —
            양식 본문의 바인딩과 1:1로 묶여 있어 다른 양식으로 옮겨갈 수 없기 때문입니다. 지금
            저장하면 <b>삽입했을 때 그 입력들이 빠진 채</b>로 들어갑니다. 재사용할 값이라면 트리에서
            추가한 입력(바인딩 배치가 아닌 것)으로 구성하세요.
          </p>
        ) : null}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="preset-name-input">
            이름
          </label>
          <input
            id="preset-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="예: 현장 담당자 선택 세트"
            className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor="preset-description-input"
          >
            설명 <span className="font-normal text-gray-400">(선택)</span>
          </label>
          <input
            id="preset-description-input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="언제 쓰는 프리셋인지 한 줄 설명"
            className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="preset-note-input">
            변경 메모
          </label>
          <input
            id="preset-note-input"
            type="text"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="예: 최초 저장"
            className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
          />
          <p className="mt-1 text-xs text-gray-400">
            버전 선택 시 판단 근거로 표시됩니다(필수).
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        {/* 본문 버튼 줄도 base의 닫기 요청 context 안이라 확인을 거친다. */}
        <ModalCancelButton onCancel={onClose} />
        <Button variant="primary" onClick={submit} disabled={!canConfirm}>
          저장
        </Button>
      </div>
    </Modal>
  );
}
